import {Subscription} from "rxjs";
import {DatePipe, NgClass} from "@angular/common";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";

import {ToastrService} from "ngx-toastr";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {CurrentUserStateService} from "@numizmat/services/account";
import {RestoreScrollPositionDirective} from "@numizmat/directives";
import {LoginModalComponent, UserReportModalComponent} from "@numizmat/modals";
import {AlertService, JsonLdService, MetaTagService, ModalService} from "@numizmat/services/common";
import {ProfileApiService, UserBlockApiService, UserFollowerApiService} from "@numizmat/services/core";
import {ForeignProfileResponse, UserBlockRequest, UserFollowerRequest} from "@numizmat/services-models/core";
import {ImageComponent, LoaderComponent, PageHeaderComponent, UserRatingComponent} from "@numizmat/components";

@Component({
  selector: 'nz-foreign-profile',
  imports: [
    LoaderComponent,
    UserRatingComponent,
    DatePipe,
    NgClass,
    RouterLink,
    RestoreScrollPositionDirective,
    ImageComponent,
    PageHeaderComponent
  ],
  templateUrl: './foreign-profile.component.html',
  styleUrl: './foreign-profile.component.scss'
})
export class ForeignProfileComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private toastrService: ToastrService = inject(ToastrService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private profileApiService: ProfileApiService = inject(ProfileApiService);
  private userBlockApiService: UserBlockApiService = inject(UserBlockApiService);
  private userFollowerApiService: UserFollowerApiService = inject(UserFollowerApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private userLoggedIn: boolean = false;
  private subs: Subscription = new Subscription();

  protected userId!: string;
  protected loading: boolean = true;
  protected loadingFollower: boolean = false;
  protected profile!: ForeignProfileResponse;
  protected profileImageUrl!: string;

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    const subscription: Subscription = this.activatedRoute.paramMap.subscribe(params => {
      this.userId = params.get('id') || '';
      if (!!this.userId) {
        this.getProfile();
        this.getCurrentUser();
      } else {
        this.router.navigate([appRoutes.root]).then();
      }
    });
    this.subs.add(subscription);
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.jsonLdService.removeSchema();
  }

  protected upsertUserFollower(): void {
    if (!this.userLoggedIn) {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
      return;
    }
    if (this.loadingFollower) {
      return;
    }
    this.loadingFollower = true;
    if (!this.profile.userFollowerId) {
      const request: UserFollowerRequest = {
        userId: this.userId
      };
      this.userFollowerApiService.insertUserFollower(request).subscribe({
        next: response => {
          this.loadingFollower = false;
          const followerCount: number = this.profile.followerCount + 1;
          this.toastrService.success(`Успешно последвахте ${this.profile.username}!`);
          this.profile = {
            ...this.profile,
            userFollowerId: response,
            followerCount: followerCount,
          };
        }
      });
    } else {
      this.userFollowerApiService.deleteUserFollower(this.profile.userFollowerId).subscribe({
        next: response => {
          this.loadingFollower = false;
          const followerCount: number = this.profile.followerCount - 1;
          this.toastrService.success(`Успешно спряхте да следвате ${this.profile.username}!`);
          this.profile = {
            ...this.profile,
            userFollowerId: undefined,
            followerCount: followerCount,
          };
        }
      });
    }
  }

  protected insertUserReport(): void {
    if (!this.userLoggedIn) {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
      return;
    }
    const modalRef: NgbModalRef = this.modalService.open(UserReportModalComponent);
    modalRef.componentInstance.userId = this.userId;
    modalRef.componentInstance.username = this.profile.username;
  }

  protected deleteUserBlock(): void {
    if (!this.userLoggedIn) {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
      return;
    }
    if (!!this.profile.userBlockId) {
      this.userBlockApiService.deleteUserBlock(this.profile.userBlockId).subscribe({
        next: response => {
          this.profile.userBlockId = undefined;
          this.toastrService.success(`Успешно премахнахте ${this.profile.username} от списъка със забранени потребители.`);
        }
      });
    }
  }

  protected showUserRatingInfo(): void {
    this.alertService.showUserRatingInfo();
  }

  protected showUserBlockWarning() {
    if (!this.userLoggedIn) {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
      return;
    }
    this.alertService.showUserBlockWarning(this.profile.username).then((isConfirmed: boolean) => {
      if (isConfirmed) {
        const request: UserBlockRequest = {
          userId: this.userId
        };
        this.userBlockApiService.insertUserBlock(request).subscribe({
          next: response => {
            this.profile.userBlockId = response;
            this.toastrService.success(`Успешно добавихте ${this.profile.username} към списъка със забранени потребители.`);
          }
        });
      }
    });
  }

  private getProfile(): void {
    this.loading = true;
    this.profileApiService.getForeignProfile(this.userId).subscribe({
      next: (response: ForeignProfileResponse) => {
        this.loading = false;
        this.profile = response;
        if (!!this.profile.userProfileImageId) {
          this.profileImageUrl = `${environment.apiImageUserProfileUrl}/${this.userId}/${this.profile.userProfileImageId}`;
        } else {
          this.profileImageUrl = environment.apiImageDefaultUserProfileUrl;
        }
        this.setSchema();
        this.setMetaTags();
      }
    });
  }

  private getCurrentUser(): void {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        this.userLoggedIn = currentUser?.loggedIn;
      }
    });
    this.subs.add(subscription);
  }

  private setSchema(): void {
    const userProfileSchema = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "name": this.profile.username,
      "description": `Профил на потребителя ${this.profile.username} в Collectoria`,
      "mainEntity": {
        "@type": "Person",
        "name": this.profile.username,
        "url": `${environment.applicationDomain}/profile/${this.userId}`,
        "identifier": this.userId,
        "aggregateRating": {
          "ratingValue": 5,
          "bestRating": 5,
          "ratingCount": 89
        },
        "interactionStatistic": [
          {
            "@type": "InteractionCounter",
            "interactionType": "https://schema.org/UseAction",
            "userInteractionCount": this.profile.activeAdCount
          },
          {
            "@type": "InteractionCounter",
            "interactionType": "https://schema.org/UseAction",
            "userInteractionCount": this.profile.inactiveAdCount
          }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `Обяви на ${this.profile.username}`,
          "url": `${environment.applicationDomain}/ad-list?sellerId=${this.userId}&sellerUsername=${this.profile.username}`
        }
      }
    };

    this.jsonLdService.setSchema(userProfileSchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.profile}/${this.userId}`);
    this.metaTagService.setTitle(`Профил на ${this.profile.username} | Collectoria`);
    this.metaTagService.setDescription(`Профил на ${this.profile.username} | Collectoria`);
  }
}
